/**
 * Shared date helpers for the treatment plan review-by cadence.
 *
 * Calendar primitives handle the date boundary. Callers choose the surface
 * (chip, tooltip, or modal header).
 */

import {
  differenceInCalendarDays,
  displayCalendarDate,
  fromPickerDate,
  localTodayCalendarDate,
  parseCalendarDate,
} from '@shared/dates/calendarDate';

/** Review-by values are calendar dates; updated-at timestamps use the browser's local day. */
function reviewCalendarDate(iso: string) {
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return parseCalendarDate(iso);
  return fromPickerDate(new Date(iso));
}

export function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const target = reviewCalendarDate(iso);
  return target ? differenceInCalendarDays(target, localTodayCalendarDate()) : null;
}

export function formatReviewDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const date = reviewCalendarDate(iso);
  return date
    ? displayCalendarDate(date, undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : null;
}

export type ReviewUrgency = 'overdue' | 'soon' | null;

/** Derive urgency from days-until-review. null means no date or plenty of runway. */
export function reviewUrgency(days: number | null): ReviewUrgency {
  if (days === null) return null;
  if (days < 0) return 'overdue';
  if (days <= 30) return 'soon';
  return null;
}
